import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {ListItemTrailingBadge} from '@github-ui/list-view/ListItemTrailingBadge'
import {UnsafeHTMLText} from '@github-ui/safe-html/UnsafeHTML'
import {clsx} from 'clsx'
import type React from 'react'
import {type ComponentProps, type Ref, type RefObject, useCallback, useMemo} from 'react'
import {graphql, type PreloadedQuery, useFragment} from 'react-relay'

import type {IssuePullRequestTitle$key} from './__generated__/IssuePullRequestTitle.graphql'
import type {IssueRowSecondaryQuery} from './__generated__/IssueRowSecondaryQuery.graphql'
import {IssueItemSubIssuesSummary} from './IssueItemSubIssuesSummary'
import styles from './IssuePullRequestTitle.module.css'
import {Label} from './labels/Label'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const titleFragment = graphql`
  fragment IssuePullRequestTitle on IssueOrPullRequest
  @argumentDefinitions(labelPageSize: {type: "Int!", defaultValue: 10}) {
    __typename
    ... on Issue {
      id
      number
      labels(first: $labelPageSize, orderBy: {field: NAME, direction: ASC}) @connection(key: "Labels_labels") {
        edges {
          node {
            ...Label
            name
            id
          }
        }
      }
    }
    ... on PullRequest {
      number
      labels(first: $labelPageSize, orderBy: {field: NAME, direction: ASC}) @connection(key: "Labels_labels") {
        edges {
          node {
            ...Label
            name
            id
          }
        }
      }
    }
  }
`

type IssuePullRequestTitleProps = {
  dataKey: IssuePullRequestTitle$key
  metadataRef?: PreloadedQuery<IssueRowSecondaryQuery> | null
  repositoryOwner: string
  repositoryName: string
  ref?: Ref<HTMLAnchorElement>
  href?: string
  target?: string
  onClick?: React.MouseEventHandler<HTMLElement>
  value: string
  leadingBadge?: ComponentProps<typeof ListItemTitle>['leadingBadge']
  /**
   * Href getter for the label badge links
   * @param name - name of the label
   * @returns URL to the label
   */
  getLabelHref: (name: string) => string
  /**
   * Href getter for the sub-issues progress badge links
   * @param owner - owner of the issue's repository
   * @param repo - name of the issue's repository
   * @param number - number of the issue
   * @returns URL with query for the sub-issues of the issue
   */
  getSubIssuesHref?: (owner: string, repo: string, number: number) => string
  headerClassName?: string
}

export function IssuePullRequestTitle({
  dataKey,
  metadataRef,
  ref,
  href,
  target,
  onClick: externalOnClick,
  value,
  leadingBadge,
  getLabelHref,
  getSubIssuesHref,
  headerClassName,
  repositoryOwner,
  repositoryName,
}: IssuePullRequestTitleProps) {
  const data = useFragment(titleFragment, dataKey)

  const {number, labels} =
    data.__typename === 'PullRequest' || data.__typename === 'Issue' ? data : {number: undefined, labels: undefined}

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // shortcircuit to default link behaviors if using middle click or modifiers
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.button === 1 || event.shiftKey || event.ctrlKey || event.metaKey) return
      externalOnClick?.(event)
    },
    [externalOnClick],
  )

  const trailingLabels = (labels?.edges || []).flatMap(a => a?.node || [])
  const trailingBadges = [
    ...trailingLabels.map(label => (
      <ListItemTrailingBadge key={label.id} title={label.name}>
        <Label label={label} getLabelHref={getLabelHref} fontWeight={500} />
      </ListItemTrailingBadge>
    )),
  ]

  if (data.__typename === 'Issue') {
    const subIssuesSummary = (
      <IssueItemSubIssuesSummary
        metadataRef={metadataRef}
        issueId={data.id}
        link={number && getSubIssuesHref ? getSubIssuesHref(repositoryOwner, repositoryName, number) : ''}
        key="sub-issues-summary"
      />
    )
    if (subIssuesSummary) trailingBadges.unshift(subIssuesSummary)
  }

  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttributes = useMemo(
    () =>
      number
        ? data.__typename === 'PullRequest'
          ? getHovercardAttributesForType('pull_request', {
              owner: repositoryOwner,
              repo: repositoryName,
              pullRequestNumber: number,
            })
          : data.__typename === 'Issue'
            ? getHovercardAttributesForType('issue', {
                owner: repositoryOwner,
                repo: repositoryName,
                issueNumber: number,
              })
            : {}
        : {},
    [data.__typename, getHovercardAttributesForType, number, repositoryName, repositoryOwner],
  )

  return (
    <ListItemTitle
      value={<UnsafeHTMLText as="span" html={value} />}
      onClick={onClick}
      leadingBadge={leadingBadge}
      trailingBadges={trailingBadges}
      headingClassName={clsx(headerClassName, styles.ListItemTitle_0)}
      anchorRef={ref as RefObject<HTMLAnchorElement | null>}
      anchorClassName={clsx(number && styles.ListItemTitle_1)}
      href={href}
      target={target}
      linkProps={{...hovercardAttributes, 'data-testid': 'issue-pr-title-link'}}
    />
  )
}
